import React, { useState } from 'react';
import { Link2, Copy, Check, Loader2 } from 'lucide-react';
import organicFetch from '../lib/organicFetch';

// Standalone crabplace.co link generator (the "Bitly" tab on the Social Media
// page). Pick a destination, add UTM tags, generate a branded short link, copy it.
// For posts done manually on any platform.
//
// ⚠️ THE SHORTENER WAS NEVER META-ONLY — the `shorten` action just takes a URL —
// but this screen READ as Meta-only and behaved that way, which is what Jessica
// asked about on 2026-09-14 ("can we add additional social media platforms... I
// want to post to Pinterest"). Two things made it Meta-shaped, and both were
// traps rather than limits:
//
//   1. utm_source was a free-text box pre-filled with "facebook". Build a
//      Pinterest link, forget to retype the source, and the orders are filed as
//      Facebook. Nothing on the screen said so. Platform is now a PICKED value,
//      not something you have to remember to overwrite.
//   2. The blurb promised every link shows up in "Meta Campaigns → Attributed
//      Orders". That was only ever true of Meta links, and it was the other half
//      of her question — whether links were being dumped into the Facebook
//      numbers regardless of where they were posted. They were, but through the
//      attribution query rather than through here: the Meta filter matched on
//      utm_medium alone with no platform check, so any link tagged cpc/paid/
//      paid_social counted as a Meta ad sale. Fixed the same day in neon.js
//      (META_PAID_SQL) and locked down by scripts/test-attribution-channels.js.
//      This screen now names the channel a link will actually report under.

const inp = { width: '100%', padding: '9px 11px', fontSize: 13, border: '1px solid var(--gray-200)', borderRadius: 6, fontFamily: 'Montserrat', color: 'var(--navy)', boxSizing: 'border-box' };
const lbl = { fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4, display: 'block' };
const sub = { textTransform: 'none', fontWeight: 400, color: 'var(--gray-400)' };

// Quick destination presets (same as the Facebook composer). Click to fill the URL.
const PRODUCT_LINKS = [
  { label: 'Steamed Crabs', path: '/steamed-maryland-blue-crabs/' },
  { label: 'Crab Cakes', path: '/crab-cakes/' },
  { label: 'Crab Meat', path: '/crab-meat/' },
  { label: 'Lobster Tails', path: '/maine-lobster-tails/' },
  { label: 'Live Crabs', path: '/live-maryland-blue-crabs/' },
];

/* Where you are putting the link. Picking one sets utm_source, so a Pinterest
   link can never go out tagged "facebook" just because that was the default.
   `paid` is the utm_medium used when the Paid ad toggle is on; `organic` when it
   is off. These values are the ones the attribution queries actually recognise
   (see CHANNEL_CASE in lib/marketing-metrics.js) — do not invent new spellings
   here, an unrecognised source falls through to "other" and the orders stop
   being countable by channel. */
const PLATFORMS = [
  { key: 'pinterest', label: 'Pinterest', source: 'pinterest', organic: 'organic_social', paid: 'paid_social' },
  { key: 'facebook',  label: 'Facebook',  source: 'facebook',  organic: 'organic_social', paid: 'paid_social' },
  { key: 'instagram', label: 'Instagram', source: 'instagram', organic: 'organic_social', paid: 'paid_social' },
  { key: 'tiktok',    label: 'TikTok',    source: 'tiktok',    organic: 'organic_social', paid: 'paid_social' },
  { key: 'youtube',   label: 'YouTube',   source: 'youtube',   organic: 'organic_social', paid: 'paid_social' },
  { key: 'x',         label: 'X / Twitter', source: 'twitter', organic: 'organic_social', paid: 'paid_social' },
  { key: 'linkedin',  label: 'LinkedIn',  source: 'linkedin',  organic: 'organic_social', paid: 'paid_social' },
  { key: 'reddit',    label: 'Reddit',    source: 'reddit',    organic: 'organic_social', paid: 'paid_social' },
  // Not social, but this generator is the only branded-link tool we have and
  // people were already using it for these.
  { key: 'email',     label: 'Email',     source: 'email',     organic: 'email',          paid: 'email' },
  { key: 'sms',       label: 'Text / SMS', source: 'dotdigital', organic: 'sms',          paid: 'sms' },
  { key: 'print',     label: 'Print / QR', source: 'print',    organic: 'offline',        paid: 'offline' },
];

/* Which channel an order from this link will be reported under. Mirrors
   CHANNEL_CASE — it exists so the screen can TELL you, rather than leaving you
   to guess whether a link lands in the Facebook numbers. Kept deliberately
   simple: it describes the tags you are about to generate, and says plainly
   when a platform has no channel page of its own yet. */
function reportsAs(source, medium) {
  const s = (source || '').toLowerCase().trim();
  const m = (medium || '').toLowerCase().trim();
  if (['google', 'google_ads', 'adwords'].includes(s)) return { channel: 'Google', where: 'Google Campaigns → Attributed Orders' };
  if (m === 'sms' || ['dotdigital', 'listrak'].includes(s) || ['email', 'newsletter'].includes(m)) {
    return { channel: m === 'sms' ? 'SMS' : 'Email', where: 'Email Campaigns → Attributed Orders' };
  }
  if (['meta', 'facebook', 'fb', 'ig', 'instagram', 'an'].includes(s)) {
    const paid = ['paid_social', 'paid', 'cpc', 'ppc', 'paidsocial'].includes(m);
    return { channel: paid ? 'Meta (paid)' : 'Meta (organic)', where: 'Social Media → Performance, and Attributed Orders' };
  }
  if (!s) return { channel: null, where: null };
  // Pinterest, TikTok, LinkedIn and friends are counted as their own channel in
  // the marketing numbers, but have no dedicated page yet — say so rather than
  // implying the orders vanish, and rather than quietly filing them under Meta.
  return {
    channel: s.charAt(0).toUpperCase() + s.slice(1),
    where: 'Marketing Brain → channel mix (no page of its own yet)',
  };
}

function buildUtmUrl(dest, params) {
  let u;
  try { u = new URL(String(dest).trim()); } catch { return null; }
  if (!/^https?:$/.test(u.protocol)) return null;
  Object.entries(params).forEach(([k, v]) => {
    const val = (v || '').trim();
    if (val) u.searchParams.set(k, val);
  });
  return u.toString();
}

export default function LinkBuilder() {
  const [dest, setDest] = useState('https://crabplace.com/');
  // No platform is chosen for you. The old default of "facebook" is exactly how
  // a Pinterest link ends up in the Facebook numbers.
  const [platform, setPlatform] = useState(null);
  const [paidAd, setPaidAd] = useState(false);
  const [source, setSource] = useState('');
  const [medium, setMedium] = useState('');
  const [campaign, setCampaign] = useState('');
  const [content, setContent] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null); // { shortLink, longUrl, via }
  const [copied, setCopied] = useState(null);

  const longPreview = buildUtmUrl(dest, { utm_source: source, utm_medium: medium, utm_campaign: campaign, utm_content: content });
  const destination = reportsAs(source, medium);

  // Picking a platform (or flipping Paid ad) writes both tags. They stay
  // editable underneath — this sets them correctly, it does not lock them.
  function choosePlatform(p, isPaid) {
    setPlatform(p.key);
    setSource(p.source);
    setMedium(isPaid ? p.paid : p.organic);
  }
  function togglePaid(next) {
    setPaidAd(next);
    const p = PLATFORMS.find((x) => x.key === platform);
    if (p) setMedium(next ? p.paid : p.organic);
  }

  async function generate() {
    setErr(null); setResult(null);
    const url = buildUtmUrl(dest, { utm_source: source, utm_medium: medium, utm_campaign: campaign, utm_content: content });
    if (!url) { setErr('Enter a valid destination URL (starting with https://).'); return; }
    setBusy(true);
    try {
      const r = await organicFetch({
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'shorten', url, campaign: campaign || null }),
      }).then(x => x.json());
      if (r.error) throw new Error(r.error);
      setResult({ shortLink: r.shortLink, longUrl: r.longUrl || url, via: r.via });
    } catch (e) { setErr(e.message || 'Could not generate the link.'); } finally { setBusy(false); }
  }

  function copy(text, which) {
    try { navigator.clipboard.writeText(text); setCopied(which); setTimeout(() => setCopied(null), 1600); } catch (e) { /* noop */ }
  }

  return (
    <div style={{ maxWidth: 660 }}>
      <div className="card" style={{ padding: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <Link2 size={18} color="var(--orange)" />
          <h3 style={{ margin: 0, color: 'var(--navy)', fontSize: 16 }}>crabplace.co link generator</h3>
        </div>
        <p style={{ fontSize: 12.5, color: 'var(--gray-500)', marginTop: 0, lineHeight: 1.5 }}>
          Build a tracked, branded <strong>crabplace.co</strong> link for a post you'll put up manually. Works for any platform, not just Facebook. Pick where it points, say where you're posting it, generate, and copy. Orders are then reported under <strong>that</strong> platform.
        </p>

        <label style={lbl}>Destination URL <span style={sub}>(where the link goes)</span></label>
        <input style={inp} value={dest} onChange={e => setDest(e.target.value)} placeholder="https://crabplace.com/steamed-maryland-blue-crabs/" />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
          {PRODUCT_LINKS.map((p) => {
            const full = `https://crabplace.com${p.path}`;
            const active = dest === full;
            return (
              <button key={p.path} type="button" onClick={() => setDest(full)}
                style={{ fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 14, cursor: 'pointer', fontFamily: 'Montserrat',
                  border: `1px solid ${active ? 'var(--navy)' : 'var(--gray-200)'}`, background: active ? 'var(--navy)' : '#fff', color: active ? '#fff' : 'var(--navy)' }}>
                {p.label}
              </button>
            );
          })}
        </div>

        <div style={{ marginTop: 14 }}>
          <label style={lbl}>Where are you posting it? <span style={sub}>(sets utm_source)</span></label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {PLATFORMS.map((p) => {
              const active = platform === p.key;
              return (
                <button key={p.key} type="button" onClick={() => choosePlatform(p, paidAd)}
                  style={{ fontSize: 11, fontWeight: 600, padding: '5px 11px', borderRadius: 14, cursor: 'pointer', fontFamily: 'Montserrat',
                    border: `1px solid ${active ? 'var(--orange)' : 'var(--gray-200)'}`, background: active ? 'var(--orange)' : '#fff', color: active ? '#fff' : 'var(--navy)' }}>
                  {p.label}
                </button>
              );
            })}
          </div>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 9, fontSize: 12, color: 'var(--navy)', cursor: 'pointer' }}>
            <input type="checkbox" checked={paidAd} onChange={(e) => togglePaid(e.target.checked)} />
            This link is going in a <strong>paid ad</strong>, not an ordinary post
          </label>
        </div>

        {/* Says outright which channel will get the credit. This is the whole
            answer to "are these being pushed into the Facebook numbers?" — you
            can see before you generate. */}
        {destination.channel ? (
          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--navy)', background: '#f0f8f3', border: '1px solid #bfe3cd', borderRadius: 6, padding: '8px 11px', lineHeight: 1.45 }}>
            Orders from this link are counted as <strong>{destination.channel}</strong>.<br />
            <span style={{ color: 'var(--gray-500)', fontSize: 11 }}>{destination.where}</span>
          </div>
        ) : (
          <div style={{ marginTop: 10, fontSize: 12, color: '#8a6d3b', background: '#fcf8e3', border: '1px solid #f0e0b0', borderRadius: 6, padding: '8px 11px', lineHeight: 1.45 }}>
            Pick a platform above, or the orders from this link can't be credited to anything.
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
          <div>
            <label style={lbl}>Campaign <span style={sub}>(utm_campaign)</span></label>
            <input style={inp} value={campaign} onChange={e => setCampaign(e.target.value)} placeholder="e.g. fathers-day-2026" />
          </div>
          <div>
            <label style={lbl}>Post name <span style={sub}>(utm_content)</span></label>
            <input style={inp} value={content} onChange={e => setContent(e.target.value)} placeholder="e.g. size-matters-video" />
          </div>
          <div>
            <label style={lbl}>Source <span style={sub}>(utm_source)</span></label>
            <input style={inp} value={source} onChange={e => { setSource(e.target.value); setPlatform(null); }} placeholder="pick a platform above" />
          </div>
          <div>
            <label style={lbl}>Medium <span style={sub}>(utm_medium)</span></label>
            <input style={inp} value={medium} onChange={e => setMedium(e.target.value)} placeholder="organic_social" />
          </div>
        </div>

        {longPreview && (
          <div style={{ fontSize: 10.5, color: 'var(--gray-500)', marginTop: 10, wordBreak: 'break-all', background: 'var(--gray-50)', padding: '7px 9px', borderRadius: 5 }}>
            Full tagged link: {longPreview}
          </div>
        )}

        <button onClick={generate} disabled={busy}
          style={{ marginTop: 14, background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 18px', fontWeight: 700, fontSize: 13, cursor: busy ? 'default' : 'pointer', fontFamily: 'Montserrat', display: 'inline-flex', alignItems: 'center', gap: 6, opacity: busy ? 0.7 : 1 }}>
          {busy ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Generating&hellip;</> : <><Link2 size={14} /> Generate crabplace.co link</>}
        </button>

        {err && <div style={{ marginTop: 10, fontSize: 12, color: '#c33' }}>{err}</div>}

        {result && (
          <div style={{ marginTop: 16, padding: 14, background: '#f0f8f3', border: '1px solid #bfe3cd', borderRadius: 8 }}>
            <label style={lbl}>Your tracked link{result.via === 'full' ? ' (Bitly unavailable — full link)' : ''}</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input readOnly value={result.shortLink} onFocus={e => e.target.select()}
                style={{ ...inp, fontWeight: 700, color: 'var(--navy)', background: '#fff' }} />
              <button onClick={() => copy(result.shortLink, 'short')}
                style={{ background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 6, padding: '9px 14px', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', fontFamily: 'Montserrat', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                {copied === 'short' ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
              </button>
            </div>
            {result.shortLink !== result.longUrl && (
              <div style={{ marginTop: 8, fontSize: 10.5, color: 'var(--gray-500)', wordBreak: 'break-all' }}>
                Points to: {result.longUrl}{' '}
                <button onClick={() => copy(result.longUrl, 'long')}
                  style={{ background: 'none', border: 'none', color: 'var(--orange)', cursor: 'pointer', fontSize: 10.5, fontWeight: 700, padding: 0 }}>
                  {copied === 'long' ? 'copied' : 'copy full link'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
